export type EventHandler<T> = (...args: T[]) => void;

export default class EventTarget {

    _eventQueue = new Map();

    addEventListener<T>(
        eventType: string | null,
        eventHandler: EventHandler<T> | null,
        options: AddEventListenerOptions = {}
    ) {
        if (!(typeof eventType === "string" && typeof eventHandler === "function")) {
            return null;
        }

        if (options.once) {
            this._autoRemoveHandler(eventType, eventHandler);

            return null;
        }

        const eventTypeQueue = this._getEventTypeQueue(eventType);
        const eventHandlerPosition = eventTypeQueue.push(eventHandler);

        return () => this._removeEventHandler(eventType, eventHandlerPosition - 1);
    }

    removeEventListener<T>(eventType: string | null, eventHandler?: EventHandler<T>) {
        if (typeof eventType !== "string") {
            return null;
        }

        if (typeof eventHandler === "function") {
            const eventHandlerPosition = this._getEventTypeQueue(eventType).indexOf(eventHandler);

            return this._removeEventHandler(eventType, eventHandlerPosition);
        }

        this._eventQueue.delete(eventType);

        return true;
    }

    dispatchEvent(eventType: string, details?: unknown) {
        if (!this._eventQueue.has(eventType)) {
            return;
        }

        const eventTypeQueue = this._getEventTypeQueue(eventType);
        let i = eventTypeQueue.length - 1;

        /**
         * [ST] Reverse while loop addresses issue of event handlers which mutate eventTypeQueue array.
         * Mutations occur when eventHandler self-unregisters with option once.
         * Using Array.forEach doesn't handle mutated array.
         */
        while (i >= 0) {
            eventTypeQueue[i](details);
            i--;
        }
    }

    private _getEventTypeQueue(eventType: string) {
        if (!this._eventQueue.has(eventType)) {
            this._eventQueue.set(eventType, []);
        }

        return this._eventQueue.get(eventType);
    }

    private _removeEventHandler(eventType: string, eventHandlerPosition: number) {
        const eventIndex = eventHandlerPosition;

        if (eventIndex < 0) {
            return false;
        }

        this._getEventTypeQueue(eventType).splice(eventIndex, 1);

        return true;
    }

    private _autoRemoveHandler<T>(eventType: string, eventHandler: EventHandler<T>) {
        const eventTypeQueue = this._getEventTypeQueue(eventType);

        const eventHandlerPosition = eventTypeQueue.push((...args: T[]) => {
            eventHandler(...args);
            this._removeEventHandler(eventType, eventHandlerPosition - 1);
        });
    }

}
